import { z } from "zod";
/**
 * Day-of-the-wedding timeline event. Stored under
 * /weddings/{weddingId}/dayPlanEvents/{eventId}.
 *
 * Time is kept as a local hour/minute pair so the schedule stays consistent
 * across timezones — the wedding day belongs to the venue's clock, not the
 * couple's device. Hour is the 24h value (0–23); the UI renders either 12h
 * or 24h based on a per-screen toggle.
 *
 * `icon` is a free-form string referencing an entry in PLAN_ICONS; unknown
 * names degrade to a dot so old/new clients survive each other.
 *
 * `personId` ties an event to a column in the multi-person calendar planner
 * (see `wedding.dayPlanPeople`). An empty string is the backward-compat
 * bucket: pre-migration events and events whose person was deleted both fall
 * back to the first column in the UI — same forgiving degradation as the
 * unknown-icon → dot rule above.
 *
 * `groupId` links the copies of a *shared* event (ceremony, dinner, …) that
 * the template duplicates into every person's column, so an "apply to
 * everyone" edit can patch the whole set in one batch.
 */
export const DayPlanEventSchema = z.object({
    id: z.string(),
    /** Visual icon key — see `apps/web/src/features/day-plan/plan-icons.tsx`. */
    icon: z.string().default("dot"),
    /** Column this event belongs to. "" = unassigned → first column in the UI. */
    personId: z.string().default(""),
    /** Hour in 24h format (0–23). */
    hour: z.number().int().min(0).max(23),
    /** Minute (0–59). */
    minute: z.number().int().min(0).max(59),
    /** Event label, e.g. "Pierwszy taniec". May be empty while editing. */
    title: z.string().default(""),
    /** Free-form notes/details for the event (logistics, contacts, reminders). */
    notes: z.string().default(""),
    /** Duration in minutes (0–999). */
    durationMinutes: z.number().int().min(0).max(999).default(0),
    /** Stable monotonic sort order; the UI sorts by hour/minute as a fallback. */
    order: z.number().int().default(0),
    /** Links duplicated shared events across columns; absent on per-person rows. */
    groupId: z.string().optional(),
    /**
     * When true the event belongs to every column at once (a shared moment such
     * as the ceremony) and renders as a single full-width bar spanning the whole
     * grid; `personId` is ignored for layout. Absent/false = normal per-column.
     */
    allColumns: z.boolean().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
